'use client';

import {useEffect, useState} from 'react';

import {getGwgAccelerator, type GwgAccelerator} from '@udemy-v2/career-accelerators';
import {useI18n} from '@udemy-v2/i18n';

import {fetcher} from '@/lib/gql-fetcher';

import {useIsGwgAvailable} from './use-is-gwg-available';

const GWG_SLUG = 'ai-professional-certificate-learning-path';

/**
 * GWG accelerator hook. Tri-state:
 *   undefined → loading (skeleton) — either eligibility is still resolving, or
 *               eligibility is confirmed and the accelerator fetch is in flight
 *   null      → ineligible, fetch failed, or no accelerator (hide section)
 *   T         → success
 */
export function useGwgAccelerator(): GwgAccelerator | undefined | null {
    const {lang} = useI18n();
    const isGwgAvailable = useIsGwgAvailable();
    const [data, setData] = useState<GwgAccelerator | undefined | null>(undefined);

    useEffect(() => {
        if (!isGwgAvailable) {
            return;
        }

        let cancelled = false;

        const fetchData = async () => {
            try {
                const result = await getGwgAccelerator(GWG_SLUG, lang, {
                    customGqlFetcher: fetcher,
                });
                if (!cancelled) {
                    setData(result ?? null);
                }
            } catch {
                if (!cancelled) setData(null);
            }
        };

        fetchData();
        return () => {
            cancelled = true;
        };
    }, [lang, isGwgAvailable]);

    if (isGwgAvailable === undefined) {
        return undefined;
    }

    return isGwgAvailable ? data : null;
}
